import React, { useState } from 'react';
import styled from 'styled-components';
import Button from '../Button';
import Input from '../Input';
import { useAuth } from '../../context/AuthContext';
import { useUser } from '../../context/UserContext';
import { useTheme } from '../../context/ThemeContext';
import { CloseOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import Message from '../Message';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding-top: 80px;

  @media (max-width: 768px) {
    padding-top: 140px;
    align-items: flex-start;
    overflow-y: auto;
  }
`;

const ModalWrapper = styled.div`
  position: relative;
  width: 447px;

  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

const ModalContent = styled.div`
  position: relative;
  width: 447px;
  background: ${({ theme }) => theme.cardBg};
  border-radius: 16px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
  overflow: visible;

  @media (max-width: 768px) {
    width: calc(100% - 32px);
    margin: 0 16px;
    height: auto;
  }

  @media (max-width: 480px) {
    padding: 32px 24px;
  }
`;

const LoginModalMessage = styled.div.attrs({ id: 'login-modal-message' })`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  pointer-events: none;
  display: flex;
  justify-content: center;
  
  .ant-message {
    position: static;
    display: flex;
    justify-content: center;
  }

  .ant-message-notice {
    padding: 0;
    margin: 0;
    display: flex;
    justify-content: center;
  }

  .ant-message-notice-content {
    display: inline-block;
    background: rgba(0, 0, 0, 0.85);
    color: white;
    border-radius: 4px;
    padding: 10px 16px;
    box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08);
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    width: calc(100% - 32px);
    top: 16px;
    padding: 0 16px;
    box-sizing: border-box;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 32px;
  right: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.text};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  height: 32px;
  
  &:hover {
    opacity: 0.6;
  }
`;

const Title = styled.h2`
  text-align: left;
  color: ${({ theme }) => theme.text};
  font-size: 20px;
  font-weight: 500;
  height: 32px;
  line-height: 32px;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
  margin-top: 32px;
`;

const StyledInput = styled(Input)`
  width: 100%;
  height: 44px;
  border-radius: 22px;
  border: none;
  background: #E8E8E8;
  padding: 0 16px;
  box-sizing: border-box;
  
  input {
    height: 44px;
    background-color: #E8E8E8 !important;
    color: #000000;
    font-size: 14px;
    width: 100%;
    box-sizing: border-box;
    border: none;
    transition: all 0.3s ease;
    outline: none;
    line-height: 44px;
    padding: 0;
    
    &::placeholder {
      color: #888888;
    }

    &:hover {
      background-color: #E8E8E8 !important;
    }

    &:focus {
      background-color: #E8E8E8 !important;
      outline: none;
    }

    /* 处理浏览器自动填充的背景色 - 强化版 */
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 1000px #E8E8E8 inset !important;
      box-shadow: 0 0 0 1000px #E8E8E8 inset !important;
      -webkit-text-fill-color: #000000 !important;
      caret-color: #000000;
      transition: background-color 5000s ease-in-out 0s, color 5000s ease-in-out 0s;
      background-color: #E8E8E8 !important;
    }
  }

  &:focus-within {
    border: 1px solid #FF4D4F;
    background: #E8E8E8 !important;
    outline: none;
    
    input {
      background-color: #E8E8E8 !important;
      outline: none;
    }
  }

  /* 移除所有蓝色阴影 */
  &,
  .ant-input,
  .ant-input-affix-wrapper,
  .ant-input-affix-wrapper-focused {
    box-shadow: none !important;
    outline: none !important;
    background-color: #E8E8E8 !important;
    color: #000000 !important;
  }

  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused,
  .ant-input-affix-wrapper:hover,
  .ant-input:focus,
  .ant-input:hover {
    box-shadow: none !important;
    outline: none !important;
    background-color: #E8E8E8 !important;
    color: #000000 !important;
  }
  
  /* 添加更强的CSS规则覆盖Chrome的自动填充样式 */
  @-webkit-keyframes autofill {
    to {
      color: #000000;
      background: #E8E8E8;
    }
  }
  
  input:-webkit-autofill {
    -webkit-animation-name: autofill;
    -webkit-animation-fill-mode: both;
  }
  
  /* 额外的CSS规则来防止浏览器自动填充样式 */
  input:-internal-autofill-selected {
    background-color: #E8E8E8 !important;
    background-image: none !important;
    color: #000000 !important;
  }
  
  /* 通用样式覆盖 */
  @-webkit-keyframes onAutoFillStart { from {} to {}}
  @keyframes onAutoFillStart { from {} to {}}
  
  input:-webkit-autofill {
    animation-name: onAutoFillStart;
    animation-fill-mode: both;
  }
  
  input:autofill {
    background-color: #E8E8E8 !important;
    color: #000000 !important;
  }

  /* 额外覆盖Ant Design内部input元素 */
  .ant-input-affix-wrapper > input.ant-input {
    color: #000000 !important;
    background-color: #E8E8E8 !important;
  }
  
  .ant-input-affix-wrapper .ant-input-suffix {
    color: #000000 !important;
  }
`;

const PasswordInput = styled(StyledInput)`
  .ant-input-suffix {
    color: rgba(0, 0, 0, 0.85);
    cursor: pointer;
    margin-right: 12px;
    font-size: 18px;
    display: flex;
    align-items: center;
    
    &:hover {
      color: rgba(0, 0, 0, 0.65);
    }

    .anticon {
      color: rgba(0, 0, 0, 0.85);
      
      &:hover {
        color: rgba(0, 0, 0, 0.65);
      }
    }
  }
`;

const ForgotPassword = styled.button`
  background: none;
  border: none;
  color: #FF4D4F;
  font-size: 14px;
  margin-left: auto;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: #FF7875;
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.inputBg || theme.cardBg};

  &:checked {
    background: #FF4D4F;
    border-color: #FF4D4F;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  height: 44px;
  border-radius: 22px;
  border: none;
  background: #FF4D4F;
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #E53E3E;
  }
  
  &:disabled {
    background: #FFB2B3;
    cursor: not-allowed;
  }
`;

const RegisterLink = styled.button`
  background: none;
  border: none;
  color: #FF4D4F;
  font-size: 14px;
  margin-top: 16px;
  cursor: pointer;
  padding: 0;
  width: 100%;
  
  &:hover {
    color: #E53E3E;
  }
`;

const LoginModal = ({ onClose, onShowRegister, onShowResetPassword }) => {
  const { isLoginVisible } = useAuth();
  const { login } = useUser();
  const { theme } = useTheme();
  const [loginName, setLoginName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const showMessage = (content, type) => {
    Message[type](content, 'login-modal-message');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 添加基本的输入验证
    if (!loginName.trim()) {
      showMessage('请输入手机号', 'error');
      return;
    }
    if (!password.trim()) {
      showMessage('请输入密码', 'error');
      return;
    }

    setLoading(true);
    
    try {
      const success = await login(loginName.trim(), password.trim());
      if (success) {
        showMessage('登录成功', 'success');
        setTimeout(() => {
          onClose();
        }, 1000);
      }
    } catch (error) {
      if (error.message === '手机号或密码错误') {
        showMessage('手机号或密码错误，请重试', 'error');
      } else {
        showMessage('登录失败，请稍后重试', 'error');
      }
      console.error('登录错误:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (!isLoginVisible) return null;

  return (
    <ModalOverlay>
      <LoginModalMessage />
      <ModalWrapper>
        <ModalContent>
          <CloseButton onClick={onClose}>
            <CloseOutlined />
          </CloseButton>
          <Title>登录</Title>
          <Form onSubmit={handleSubmit}>
            <StyledInput
              type="text"
              placeholder="手机号"
              value={loginName}
              onChange={(e) => setLoginName(e.target.value)}
              required
            />
            <StyledInput
              type={showPassword ? "text" : "password"}
              placeholder="密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              suffix={
                <div onClick={togglePasswordVisibility} style={{ cursor: 'pointer' }}>
                  {showPassword ? 
                    <EyeInvisibleOutlined style={{ fontSize: '18px', color: 'black' }} /> : 
                    <EyeOutlined style={{ fontSize: '18px', color: 'black' }} />
                  }
                </div>
              }
            />
            <ForgotPassword type="button" onClick={onShowResetPassword}>
              忘记密码？
            </ForgotPassword>
            <LoginButton type="submit" disabled={loading}>
              {loading ? '登录中...' : '登录'}
            </LoginButton>
            <RegisterLink type="button" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (typeof onShowRegister === 'function') {
                onShowRegister();
              }
            }}>
              注册账号
            </RegisterLink>
          </Form>
        </ModalContent>
      </ModalWrapper>
    </ModalOverlay>
  );
};

export default LoginModal; 